import React from 'react';
import { Navigate } from 'react-router';
import { useRolePermissions } from '@rfacil/shared/hooks';

interface PermissionRouteGuardProps {
  /**
   * Nombre de la sección a verificar (ej: 'GestionarTipoDocumento', 'ListadoFacturas', etc.)
   */
  requiredSection?: string;
  /**
   * Sección alternativa (OR): acceso si cumple requiredSection o alternativeSection.
   * Ej.: ListadoFacturas | FacturasVisor para el módulo Materialidad.
   */
  alternativeSection?: string;
  /**
   * Roles permitidos para acceder (alternativa o complemento a requiredSection)
   */
  allowedRoles?: Array<'Super' | 'Corporativo' | 'Empresa' | 'Operativo'>;
  /**
   * Si es true, requiere que el usuario tenga TODOS los roles (por defecto: false, solo necesita uno)
   */
  requireAllRoles?: boolean;
  /**
   * Ruta a la que redirigir si no tiene permisos (por defecto: '/')
   */
  redirectTo?: string;
  /**
   * Contenido a mostrar si el usuario tiene permisos
   */
  children: React.ReactNode;
}

/**
 * @component PermissionRouteGuard
 * @description Componente que protege rutas verificando permisos de secciones y/o roles.
 * Si el usuario no tiene los permisos necesarios, lo redirige automáticamente.
 *
 * Este componente es diferente de RoleGuard y PermissionGuard porque:
 * - Redirige en lugar de mostrar un mensaje
 * - Se usa directamente en las rutas para prevenir acceso por URL
 * - Verifica permisos de secciones usando el sistema de permisos del hook useRolePermissions
 *
 * @example
 * ```tsx
 * <Route
 *   path="/gestionar-tipo-documento"
 *   element={
 *     <ProtectedRoute>
 *       <PermissionRouteGuard requiredSection="GestionarTipoDocumento">
 *         <GestionTipoDocumento />
 *       </PermissionRouteGuard>
 *     </ProtectedRoute>
 *   }
 * />
 * ```
 *
 * @example
 * ```tsx
 * <Route
 *   path="/gestionar-corporativo"
 *   element={
 *     <ProtectedRoute>
 *       <PermissionRouteGuard allowedRoles={['Super']}>
 *         <GestionCorporativos />
 *       </PermissionRouteGuard>
 *     </ProtectedRoute>
 *   }
 * />
 * ```
 */
export const PermissionRouteGuard: React.FC<PermissionRouteGuardProps> = ({
  requiredSection,
  alternativeSection,
  allowedRoles,
  requireAllRoles = false,
  redirectTo = '/',
  children,
}) => {
  const {
    hasSectionPermission,
    hasAnyRole,
    hasAllRoles,
    canViewDocumentType,
    canViewInvoices,
    canViewInvoicesVisor,
    canViewProviderUserRelation,
    canViewProviderType,
    canViewCorporates,
    canViewCompanies,
    canViewUsers,
    canViewRoles,
    canViewRelacionFacturaManual,
    canViewManagementSection,
    canViewAuditoria,
  } = useRolePermissions();

  const resolveSectionAccess = (section: string): boolean => {
    switch (section) {
      case 'GestionarTipoDocumento':
        return canViewDocumentType;
      case 'ListadoFacturas':
        return canViewInvoices;
      case 'FacturasVisor':
        return canViewInvoicesVisor;
      case 'RelacionUsuarioProveedor':
        return canViewProviderUserRelation;
      case 'TipoProveedor':
        return canViewProviderType;
      case 'Corporativos':
        return canViewCorporates;
      case 'Empresas':
        return canViewCompanies;
      case 'Usuarios':
        return canViewUsers;
      case 'Roles':
        return canViewRoles;
      case 'RelacionFacturaManual':
        return canViewRelacionFacturaManual;
      case 'GestionSistema':
        return canViewManagementSection;
      case 'Auditoria':
        return canViewAuditoria;
      default:
        return hasSectionPermission(section);
    }
  };

  // Verificar permisos de sección si se especifica
  let hasSectionAccess = true;
  if (requiredSection || alternativeSection) {
    const primary = requiredSection
      ? resolveSectionAccess(requiredSection)
      : false;
    const alternate = alternativeSection
      ? resolveSectionAccess(alternativeSection)
      : false;
    if (requiredSection && alternativeSection) {
      hasSectionAccess = primary || alternate;
    } else {
      hasSectionAccess = requiredSection ? primary : alternate;
    }
  }

  // Verificar roles si se especifican
  let hasRoleAccess = true;
  if (allowedRoles && allowedRoles.length > 0) {
    hasRoleAccess = requireAllRoles
      ? hasAllRoles(allowedRoles)
      : hasAnyRole(allowedRoles);
  }

  // El usuario debe tener acceso tanto a la sección como al rol (si se especifican)
  const hasAccess = hasSectionAccess && hasRoleAccess;

  // Si no tiene acceso, redirigir
  if (!hasAccess) {
    return <Navigate to={redirectTo} replace />;
  }

  return children;
};

export default PermissionRouteGuard;
